import React, { useState, useEffect } from 'react';
import { useTerugDiepte } from '../../hooks/useTerugKnop';
import { useParams, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functionsEu as functions } from '../../firebase';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { useIntl } from 'react-intl';
import { calculateAvailableTimes, getAvailableDates } from '../../utils/bookingSlots';
import { fetchCombinedAvailabilities } from '../../utils/weeklySchedule';
import './PublicBookingPage.css';
import '../Settings/GlobalNotices.css';

// Publieke, niet-ingelogde pagina waarmee een klant zijn eigen boeking kan
// bekijken/annuleren/verzetten via de link uit de bevestigingsmail
// (bookingId + manageToken). Zie functions/src/functions/booking/manageBooking.js
// voor de server-side validatie (token-check + termijn-check gebeurt daar,
// nooit enkel hier client-side).

function ManageBookingPage() {
    const { bookingId } = useParams();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const intl = useIntl();

    const [status, setStatus] = useState('loading'); // loading | error | loaded | cancelled | rescheduled
    const [errorMessage, setErrorMessage] = useState('');
    const [booking, setBooking] = useState(null);

    const [mode, setMode] = useState('view'); // view | reschedule
    const [availabilities, setAvailabilities] = useState([]);
    const [services, setServices] = useState([]);
    const [bookedSlots, setBookedSlots] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState('');
    const [availableTimes, setAvailableTimes] = useState([]);
    const [minimumBookingNoticeHours, setMinimumBookingNoticeHours] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
    // De terugknop van het toestel sluit het bevestigingsvenster of gaat uit het verzetscherm
    // terug, behalve in een iframe op de site van de aanbieder (daar zou het de geschiedenis van
    // die site vervuilen).
    const inIframe = window.self !== window.top;
    useTerugDiepte(!inIframe && mode === 'reschedule' ? 1 : 0, () => setMode('view'));
    useTerugDiepte(!inIframe && confirmCancelOpen ? 1 : 0, () => setConfirmCancelOpen(false));
    const [notification, setNotification] = useState(null);

    // Eigen in-app melding i.p.v. de kale browser-alert() — zelfde stijl/kleuren
    // als de rest van de app (GlobalNotices.css).
    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => setNotification(null), 4000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    // Escape sluit de bevestiging van het annuleren.
    useEffect(() => {
        if (!confirmCancelOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setConfirmCancelOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [confirmCancelOpen]);

    // Zelfde brandingsysteem als PublicBookingPage.js (logo/kleuren/eigen titel,
    // enkel Premium/tester) — hiervoor ontbrak deze pagina eerder volledig,
    // vandaar de kale, ongestylede tekst die de klant hier te zien kreeg.
    const [logoUrl, setLogoUrl] = useState('');
    const [customTitle, setCustomTitle] = useState('');
    const [isPremiumOrTester, setIsPremiumOrTester] = useState(false);
    const [brandingSettings, setBrandingSettings] = useState({
        primaryColor: '#000000',
        secondaryColor: '#ffffff',
        font: 'Arial'
    });

    useEffect(() => {
        const load = async () => {
            if (!bookingId || !token) {
                setStatus('error');
                setErrorMessage(intl.formatMessage({ id: 'manageBooking.invalidLink' }));
                return;
            }
            try {
                const getManageableBooking = httpsCallable(functions, 'getManageableBooking');
                const result = await getManageableBooking({ bookingId, token });
                setBooking(result.data);
                setStatus('loaded');
            } catch (error) {
                console.error('Error fetching manageable booking:', error);
                setStatus('error');
                setErrorMessage(error.message || intl.formatMessage({ id: 'manageBooking.invalidLink' }));
            }
        };
        load();
    }, [bookingId, token]);

    useEffect(() => {
        const loadBranding = async () => {
            if (!booking?.userId) return;
            try {
                // users/{uid} is niet meer publiek leesbaar: enkel de publiek
                // bedoelde huisstijl komt via een Cloud Function.
                const { data: info } = await httpsCallable(functions, 'getProviderPublicInfo')({ userId: booking.userId });
                const premiumOrTester = info.isPremiumOrTester;
                setIsPremiumOrTester(premiumOrTester);
                setLogoUrl(info.logoUrl || '');
                setCustomTitle(info.customTitle || '');
                if (premiumOrTester && info.brandingSettings) {
                    setBrandingSettings(info.brandingSettings);
                }
            } catch (error) {
                console.error('Error loading branding:', error);
            }
        };
        loadBranding();
    }, [booking?.userId]);

    const getPageStyle = () => isPremiumOrTester
        ? { fontFamily: brandingSettings.font, backgroundColor: brandingSettings.secondaryColor, color: brandingSettings.primaryColor }
        : {};

    const getButtonStyle = () => isPremiumOrTester
        ? { backgroundColor: brandingSettings.primaryColor, color: brandingSettings.secondaryColor }
        : {};

    const startReschedule = async () => {
        setMode('reschedule');
        try {
            const getBookedSlotsForProvider = httpsCallable(functions, 'getBookedSlotsForProvider');
            // Een boeking bij een teamlid staat in de agenda van dat teamlid: daar de
            // beschikbaarheid en de bezette tijden van ophalen, zoals de server bij het verzetten controleert.
            const teamMemberId = booking.teamMemberId || null;
            const [combinedAvailabilities, serviceSnap, bookedResult, providerInfo] = await Promise.all([
                fetchCombinedAvailabilities(teamMemberId ? `users/${booking.userId}/teamMembers/${teamMemberId}` : `users/${booking.userId}`),
                getDoc(doc(db, `users/${booking.userId}/services/${booking.serviceId}`)),
                getBookedSlotsForProvider({ userId: booking.userId, teamMemberId }),
                httpsCallable(functions, 'getProviderPublicInfo')({ userId: booking.userId })
            ]);

            setMinimumBookingNoticeHours(providerInfo.data.minimumBookingNoticeHours || 0);

            setAvailabilities(combinedAvailabilities);
            setServices(serviceSnap.exists() ? [{ id: serviceSnap.id, ...serviceSnap.data() }] : [{ id: booking.serviceId, duration: booking.duration, maxParticipants: 1 }]);
            // 'bookings' is niet meer rechtstreeks publiek leesbaar (zie
            // firestore.rules) — enkel datum/tijd + duur komen terug, geen
            // klantgegevens, en dat is exact wat calculateAvailableTimes nodig heeft.
            // De eigen boeking telt niet mee als bezet: de server sluit hem bij het verzetten ook
            // uit, anders zou een nieuw tijdstip dat het huidige overlapt ten onrechte verdwijnen.
            const ownStart = new Date(booking.dateTime).getTime();
            let ownRemoved = false;
            setBookedSlots((bookedResult.data.slots || [])
                .filter(slot => {
                    if (!ownRemoved && new Date(slot.dateTime).getTime() === ownStart && slot.duration === booking.duration) {
                        ownRemoved = true;
                        return false;
                    }
                    return true;
                })
                .map(slot => ({
                    dateTime: { seconds: Math.floor(new Date(slot.dateTime).getTime() / 1000) },
                    duration: slot.duration
                })));
        } catch (error) {
            console.error('Error loading reschedule data:', error);
        }
    };

    useEffect(() => {
        if (mode === 'reschedule' && selectedDate && booking) {
            setAvailableTimes(calculateAvailableTimes(selectedDate, booking.serviceId, { availabilities, services, bookedSlots, minimumNoticeHours: minimumBookingNoticeHours }));
        }
    }, [selectedDate, availabilities, services, bookedSlots, minimumBookingNoticeHours]);

    // Eigen bevestigingsvenster i.p.v. window.confirm, zodat het in de stijl van de pagina staat
    // en ook binnen een iframe betrouwbaar werkt.
    const handleCancel = async () => {
        setConfirmCancelOpen(false);
        setIsSubmitting(true);
        try {
            const cancelBookingSelfService = httpsCallable(functions, 'cancelBookingSelfService');
            await cancelBookingSelfService({ bookingId, token });
            setStatus('cancelled');
        } catch (error) {
            console.error('Error cancelling booking:', error);
            setNotification({ type: 'error', message: error.message || intl.formatMessage({ id: 'manageBooking.actionFailed' }) });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReschedule = async () => {
        if (!selectedDate || !selectedTime) return;
        setIsSubmitting(true);
        try {
            const [hours, minutes] = selectedTime.split(':');
            const newDateTime = new Date(selectedDate);
            newDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

            const rescheduleBookingSelfService = httpsCallable(functions, 'rescheduleBookingSelfService');
            await rescheduleBookingSelfService({ bookingId, token, newDateTime: newDateTime.toISOString() });
            setStatus('rescheduled');
        } catch (error) {
            console.error('Error rescheduling booking:', error);
            setNotification({ type: 'error', message: error.message || intl.formatMessage({ id: 'manageBooking.actionFailed' }) });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (status === 'loading') {
        return <div className="public-booking-page"><div className="manage-booking-card"><p>{intl.formatMessage({ id: 'loading' })}</p></div></div>;
    }

    if (status === 'error') {
        return (
            <div className="public-booking-page" style={getPageStyle()}>
                <div className="manage-booking-card" style={getPageStyle()}>
                    <p>{errorMessage}</p>
                </div>
            </div>
        );
    }

    if (status === 'cancelled') {
        return (
            <div className="public-booking-page" style={getPageStyle()}>
                {logoUrl && <div className="company-logo"><img src={logoUrl} alt="Company Logo" /></div>}
                <div className="manage-booking-card" style={getPageStyle()}>
                    <h2 className="page-title">{intl.formatMessage({ id: 'manageBooking.cancelledTitle' })}</h2>
                    <p>{intl.formatMessage({ id: 'manageBooking.cancelledInfo' })}</p>
                </div>
            </div>
        );
    }

    if (status === 'rescheduled') {
        return (
            <div className="public-booking-page" style={getPageStyle()}>
                {logoUrl && <div className="company-logo"><img src={logoUrl} alt="Company Logo" /></div>}
                <div className="manage-booking-card" style={getPageStyle()}>
                    <h2 className="page-title">{intl.formatMessage({ id: 'manageBooking.rescheduledTitle' })}</h2>
                    <p>{intl.formatMessage({ id: 'manageBooking.rescheduledInfo' })}</p>
                </div>
            </div>
        );
    }

    const bookingDate = new Date(booking.dateTime);

    return (
        <div className="public-booking-page" style={getPageStyle()}>
            {notification && (
                <div className={`notification notification-${notification.type}`}>
                    {notification.message}
                </div>
            )}
            {logoUrl && (
                <div className="company-logo">
                    <img src={logoUrl} alt="Company Logo" />
                </div>
            )}
            <div className="manage-booking-card" style={getPageStyle()}>
                {isPremiumOrTester && customTitle && (
                    <p style={{ textAlign: 'center', fontWeight: 600, marginBottom: '4px' }}>{customTitle}</p>
                )}
                <h2 className="page-title">{booking.serviceName}</h2>
                <p style={{ textAlign: 'center' }}>{bookingDate.toLocaleString(intl.locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>

                {!booking.canModify && (
                    <p className="manage-booking-notice">
                        {intl.formatMessage({ id: 'manageBooking.cutoffPassed' }, { hours: booking.cutoffHours })}
                    </p>
                )}

                {booking.canModify && mode === 'view' && (
                    <div className="manage-booking-actions">
                        <button onClick={startReschedule} disabled={isSubmitting} style={getButtonStyle()}>
                            {intl.formatMessage({ id: 'manageBooking.rescheduleButton' })}
                        </button>
                        <button onClick={() => setConfirmCancelOpen(true)} disabled={isSubmitting} className="manage-booking-cancel">
                            {intl.formatMessage({ id: 'manageBooking.cancelButton' })}
                        </button>
                    </div>
                )}

                {booking.canModify && mode === 'reschedule' && (
                    <div className="manage-booking-reschedule">
                        <DatePicker
                            selected={selectedDate}
                            onChange={(date) => { setSelectedDate(date); setSelectedTime(''); }}
                            includeDates={getAvailableDates(booking.serviceId, { availabilities, services, bookedSlots, minimumNoticeHours: minimumBookingNoticeHours })}
                            minDate={new Date()}
                            inline
                        />
                        {selectedDate && (
                            <div className="available-times">
                                {availableTimes.length === 0 && <p>{intl.formatMessage({ id: 'publicBooking.noTimesAvailable' })}</p>}
                                {availableTimes.map(time => (
                                    <button
                                        key={time}
                                        onClick={() => setSelectedTime(time)}
                                        className={selectedTime === time ? 'selected-time' : ''}
                                        style={selectedTime === time ? getButtonStyle() : {}}
                                    >
                                        {time}
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="manage-booking-actions">
                            <button onClick={() => setMode('view')} disabled={isSubmitting}>
                                {intl.formatMessage({ id: 'manageBooking.back' })}
                            </button>
                            <button onClick={handleReschedule} disabled={isSubmitting || !selectedTime} style={getButtonStyle()}>
                                {intl.formatMessage({ id: 'manageBooking.confirmReschedule' })}
                            </button>
                        </div>
                    </div>
                )}
            </div>
            {confirmCancelOpen && (
                <div className="manage-booking-dialog-overlay" onClick={() => setConfirmCancelOpen(false)}>
                    <div
                        className="manage-booking-dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="manage-booking-confirm-text"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <p id="manage-booking-confirm-text">{intl.formatMessage({ id: 'manageBooking.confirmCancel' })}</p>
                        <div className="manage-booking-actions">
                            <button onClick={() => setConfirmCancelOpen(false)} autoFocus>
                                {intl.formatMessage({ id: 'manageBooking.confirmCancelNo' })}
                            </button>
                            <button onClick={handleCancel} className="manage-booking-cancel">
                                {intl.formatMessage({ id: 'manageBooking.confirmCancelYes' })}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ManageBookingPage;
